<!--
  ~ Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
  ~ This software is the proprietary information of NEXTWELL INTL SRL.
  ~ Use is subject to license and non-disclosure terms
  -->

<ng-container *ngIf="!displayLegalEntityFields; else legalEntityFields">
  <div class="row person-autocomplete mx-none">
    <div class="col-12">
      <p class="label company">{{ 'legalEntity.autocompletePlaceholder' | translate }}</p>

      <div class="row mb-sm mx-none">
        <div class="col-12 col-lg-12">
          <div class="row">
            <div class="col-12 col-md-12 mb-sm px-none">
              <well-legal-entity-autocomplete
                placeholder="{{ 'legalEntity.autocomplete' | translate }}"
                [searchLocation]="legalEntitySearchLocation"
                (addCompany)="addNewCompany()"
                (legalEntitySelect)="onLegalEntitySelect($event)" data-testid="legal-entity-autocomplete">
              </well-legal-entity-autocomplete>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex align-items-center color-gray">
        <i class="ui-icon-warning mr-xs"></i>
        <label class="anaf-company-label">{{ 'legalEntity.existentAnafCompanyLabel' | translate }}</label>
      </div>

      <ng-container *ngIf="form?.errors && form.dirty">
        <p class="text-danger">
          {{ 'legalEntity.error.noLegalEntityCreated' | translate }}
        </p>
      </ng-container>
    </div>
  </div>
</ng-container>
<ng-template #legalEntityFields>
  <form *ngIf="form" [formGroup]="form" (ngSubmit)="onSubmit()">
    <div class="row mx-none">
      <div class="col-12 col-lg-12">
        <div class="form-container fields mb-sm">
          <div class="close-button" *ngIf="!legalEntity?.uuid">
            <button
              pButton
              icon="ui-icon-arrow-back"
              label="{{ 'legalEntity.closeInputs' | translate }}"
              class="flat close mb"
              (click)="hideLegalEntityFields()" data-testid="hide-legal-entity-fields-btn"></button>
          </div>

          <div class="row pb">
            <div class="col-12 col-lg-6 form-field">
              <p-floatlabel variant="on">
                <input
                  id="companyName"
                  formControlName="companyName"
                  formInlineError="legalEntity.error"
                  pInputText
                  wellInputTrim
                  type="text"
                  style="width: 100%" data-testid="company-name-input"/>
                <label for="companyName">{{ 'legalEntity.inputs.companyName' | translate }} *</label>
              </p-floatlabel>
            </div>
          </div>

          <div class="row pb">
            <div class="col-12 col-lg-6 form-field">
              <p-floatlabel variant="on">
                <input
                  id="uniqueIdNumber"
                  formControlName="uniqueIdNumber"
                  formInlineError="legalEntity.error"
                  pInputText
                  [readonly]="!addManualCompany"
                  [pattern]="uniqueIdNumberPattern"
                  wellInputTrim
                  type="text"
                  style="width: 100%" data-testid="unique-id-number-input"/>
                <label for="uniqueIdNumber">{{ 'legalEntity.inputs.uniqueIdNumber' | translate }} *</label>
              </p-floatlabel>
            </div>
          </div>

          <div class="row pb">
            <div class="col-12 col-lg-6 form-field">
              <p-floatlabel variant="on">
                <input
                  id="registrationCode"
                  formControlName="registrationCode"
                  pInputText
                  type="text"
                  style="width: 100%"
                  wellInputTrim data-testid="registration-code-input"/>
                <label for="registrationCode">{{ 'legalEntity.inputs.registrationCode' | translate }}</label>
              </p-floatlabel>
            </div>
          </div>

          <div class="row pb">
            <div class="col-12 col-lg-6 form-field">
              <p-floatlabel variant="on">
                <well-resource-id-dropdown
                  id="country"
                  formControlName="country"
                  formInlineError="legalEntity.error"
                  labelField="name"
                  idField="code"
                  label="{{ ('legalEntity.inputs.country' | translate) + ' *' }}"
                  [resourceType]="countryResourceType" data-testid="country-dropdown">
                </well-resource-id-dropdown>
              </p-floatlabel>
            </div>
          </div>

          <div class="row pb">
            <div class="col-12 col-lg-6 form-field">
              <p-floatlabel variant="on">
                <input
                  id="mainPhone"
                  formControlName="mainPhone"
                  pInputText
                  type="text"
                  style="width: 100%"
                  wellInputTrim data-testid="main-phone-input"/>
                <label for="mainPhone">{{ 'legalEntity.inputs.mainPhone' | translate }}</label>
              </p-floatlabel>
            </div>
          </div>

          <div class="row pb">
            <div class="col-12 col-lg-6 form-field">
              <p-floatlabel variant="on">
                <input
                  id="mainEmailAddress"
                  formControlName="mainEmailAddress"
                  pInputText
                  type="text"
                  style="width: 100%"
                  wellInputTrim data-testid="main-email-address-input"/>
                <label for="mainEmailAddress">{{ 'legalEntity.inputs.mainEmailAddress' | translate }}</label>
              </p-floatlabel>
            </div>
          </div>

          <div class="row pb">
            <div class="col-12 col-lg-6 form-field">
              <well-select-items-dropdown
                formControlName="industryTypeValue"
                [style]="{ width: '100%' }"
                [field]="'industryTypeValue'"
                [label]="'legalEntity.inputs.industryType' | translate" data-testid="industry-type-value-dropdown"></well-select-items-dropdown>
            </div>
          </div>

          <div class="row pb">
            <div class="col-12 col-lg-6 form-field">
              <p-floatlabel variant="on">
                <p-select
                  [autoDisplayFirst]="false"
                  [options]="languages"
                  formControlName="preferredLanguage"
                  formInlineError="legalEntity.error"
                  [style]="{ width: '100%' }"
                  id="language" data-testid="preferred-language-select">
                </p-select>
                <label for="language">{{ 'legalEntity.inputs.preferredLanguage' | translate }} *</label>
              </p-floatlabel>
            </div>
          </div>

          <div class="row pb">
            <div class="col-12 col-lg-6 form-field">
              <div class="-field-checkbox">
                <p-checkbox
                  id="vatRegistrationFormControl"
                  [formControl]="vatRegistrationFormControl"
                  binary="true"
                  wellDialogInsertion
                  [dialogData]="getDumpDialogData()" data-testid="vat-registration-form-control-checkbox"></p-checkbox>
                <label class="ml" for="vatRegistrationFormControl">{{
                  'legalEntity.inputs.vatRegistration' | translate
                }}</label>
              </div>
            </div>
          </div>

          <div class="row pb">
            <div class="col-12 col-lg-6 form-field">
              <div class="-field-checkbox">
                <p-checkbox
                  id="vatSplitStatusFormControl"
                  [formControl]="vatSplitStatusFormControl"
                  binary="true"
                  wellDialogInsertion
                  [dialogData]="getDumpDialogData()" data-testid="vat-split-status-form-control-checkbox"></p-checkbox>
                <label class="ml" for="vatSplitStatusFormControl">{{
                  'legalEntity.inputs.vatSplitStatus' | translate
                }}</label>
              </div>
            </div>
          </div>

          <div class="row pb">
            <div class="col-12 col-lg-6 form-field">
              <div class="-field-checkbox">
                <p-checkbox
                  id="vatInactiveStatusFormControl"
                  [formControl]="vatInactiveStatusFormControl"
                  binary="true"
                  wellDialogInsertion
                  [dialogData]="getDumpDialogData()" data-testid="vat-inactive-status-form-control-checkbox"></p-checkbox>
                <label class="ml" for="vatInactiveStatusFormControl">{{
                  'legalEntity.inputs.vatInactiveStatus' | translate
                }}</label>
              </div>
            </div>
          </div>

          <ng-container *ngIf="viewAddressFields">
            <p-divider align="left">
              <p>{{ translationSchema.legalEntity.inputs.officeAddress | translate }} (*)</p>
            </p-divider>
            <div class="row pb">
              <div class="col-12 col-lg-6 form-field">
                <p-floatlabel variant="on">
                  <well-address-picker
                    id="officeAddressDTO"
                    formControlName="officeAddressDTO"
                    formInlineError="legalEntity.error"
                    [searchLocal]="false"
                    [validateForDistance]="false"
                    [displayTypeField]="false"
                    [hideFieldsUntilInput]="true"
                    [displayVisibilityField]="false"
                    [dirty]="form?.dirty"
                    [touched]="form?.touched"
                    [showOfficeAddressInfoMessage]="true"
                    [companyRequired]="true" data-testid="office-address-dto-picker">
                  </well-address-picker>
                </p-floatlabel>
              </div>
            </div>

            <p-divider align="left">
              <p>{{ translationSchema.legalEntity.inputs.financialAddress | translate }} (*)</p>
            </p-divider>
            <div class="row pb">
              <div class="col-12 col-lg-6 form-field">
                <p-floatlabel variant="on">
                  <well-address-picker
                    id="financialAddressDTO"
                    formControlName="financialAddressDTO"
                    formInlineError="legalEntity.error"
                    [searchLocal]="false"
                    [validateForDistance]="false"
                    [displayTypeField]="false"
                    [hideFieldsUntilInput]="true"
                    [displayVisibilityField]="false"
                    [dirty]="form?.dirty"
                    [touched]="form?.touched"
                    [companyRequired]="true" data-testid="financial-address-dto-picker">
                  </well-address-picker>
                </p-floatlabel>
              </div>
            </div>
          </ng-container>

          <div class="row pb">
            <div class="col-12 col-lg-6 form-field">
              <p-floatlabel variant="on">
                <p-datePicker
                  [readonlyInput]="true"
                  id="vatInactivationDate"
                  styleClass="col-12"
                  formControlName="vatInactivationDate"
                  dataType="string"
                  appendTo="body"
                  dateFormat="yy-mm-dd" data-testid="vat-inactivation-date-datepicker">
                </p-datePicker>
                <label for="vatInactivationDate">{{ 'legalEntity.inputs.vatInactivationDate' | translate }}</label>
              </p-floatlabel>
            </div>
          </div>

          <div class="row pb" *ngIf="showEmailImportSettings">
            <div class="col-12 col-lg-6 form-field">
              <p-floatlabel variant="on">
                <textarea
                  id="emailImportSettings"
                  pTextarea
                  type="text"
                  autoResize="autoResize"
                  [rows]="3"
                  formControlName="emailImportSettings"
                  class="full-width" data-testid="email-import-settings-textarea">
                </textarea>
                <label for="emailImportSettings">{{ 'legalEntity.inputs.emailImportSettings' | translate }}</label>
              </p-floatlabel>
            </div>
            <div class="col-12">
              {{ emailSettingsSuggestion }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</ng-template>


